import { ICompanyRestClient } from "./i-company-rest.client";
import { ICompany } from "../../models/i-company";
import { ICompanyMember } from "../../models/i-company-member";
import { HostUtil } from "../../utils/host-util";
import { firstValueFrom, Observable } from "rxjs";
import { HttpClient } from "@angular/common/http";
import { ARestClient, ICreateRecordResponse, IOkResponse, IPage } from "@zaeper/communication-lib";
import { Injectable } from "@angular/core";
import { ICreateCompanyForm } from "./forms/i-create-company-form";
import { IGetCompanyPageForm } from "./forms/i-get-company-page-form";
import { IUpdateCompanyForm } from "./forms/i-update-company-form";
import { IJoinCode } from "../../models/i-join-code";
import { IJoinRequest } from "../../models/i-join-request";
import { ICompanyInvitation } from "../../models/i-company-invitation";
import { IInviteForm } from "./forms/i-invite-form";
import { ISubmitJoinRequestForm } from "./forms/i-submit-join-request-form";

@Injectable()
export class CompanyRestClient extends ARestClient implements ICompanyRestClient {
	constructor(private readonly _httpClient: HttpClient) {
		super();
	}

	getCompanyPage(form: IGetCompanyPageForm): Promise<IPage<ICompany>> {
		const endpointUrl: string = super.getEndpointUrl(HostUtil.getCompanyApiEndpointUrl(), "v1/company/page");

		return firstValueFrom(this._httpClient.get(endpointUrl, {
			withCredentials: true,
			params: {
				pageNumber: form.pageNumber,
				pageSize: form.pageSize
			}
		}) as Observable<IPage<ICompany>>);
	}

	getCompany(companyId: string): Promise<ICompany> {
		const endpointUrl: string = super.getEndpointUrl(HostUtil.getCompanyApiEndpointUrl(), `v1/company/${companyId}`);

		return firstValueFrom(this._httpClient.get(endpointUrl, { withCredentials: true }) as Observable<ICompany>);
	}

	createCompany(form: ICreateCompanyForm): Promise<ICreateRecordResponse> {
		const endpointUrl: string = super.getEndpointUrl(HostUtil.getCompanyApiEndpointUrl(), "v1/company");

		return firstValueFrom(this._httpClient.post(endpointUrl, form, { withCredentials: true }) as Observable<ICreateRecordResponse>);
	}

	updateCompany(form: IUpdateCompanyForm): Promise<IOkResponse> {
		const endpointUrl: string = super.getEndpointUrl(HostUtil.getCompanyApiEndpointUrl(), `v1/company/${form.companyId}`);

		return firstValueFrom(this._httpClient.put(endpointUrl, form, { withCredentials: true }) as Observable<IOkResponse>);
	}

	deleteCompany(companyId: string): Promise<IOkResponse> {
		const endpointUrl: string = super.getEndpointUrl(HostUtil.getCompanyApiEndpointUrl(), `v1/company/${companyId}`);

		return firstValueFrom(this._httpClient.delete(endpointUrl, { withCredentials: true }) as Observable<IOkResponse>);
	}

	removeMember(companyId: string, userId: string): Promise<IOkResponse> {
		const endpointUrl: string = super.getEndpointUrl(HostUtil.getCompanyApiEndpointUrl(), `v1/company/${companyId}/member/${userId}`);

		return firstValueFrom(this._httpClient.delete(endpointUrl, { withCredentials: true }) as Observable<IOkResponse>);
	}

	changeRole(companyId: string, userId: string, role: string): Promise<IOkResponse> {
		const endpointUrl: string = super.getEndpointUrl(HostUtil.getCompanyApiEndpointUrl(), `v1/company/${companyId}/member/${userId}/role`);

		return firstValueFrom(this._httpClient.put(endpointUrl, null, {
			withCredentials: true,
			params: { role }
		}) as Observable<IOkResponse>);
	}

	getMembers(companyId: string): Promise<ICompanyMember[]> {
		const endpointUrl: string = super.getEndpointUrl(HostUtil.getCompanyApiEndpointUrl(), `v1/company/${companyId}/members`);

		return firstValueFrom(this._httpClient.get(endpointUrl, { withCredentials: true }) as Observable<ICompanyMember[]>);
	}

	generateJoinCode(companyId: string): Promise<IJoinCode> {
		const endpointUrl: string = super.getEndpointUrl(HostUtil.getCompanyApiEndpointUrl(), `v1/company/${companyId}/join-code`);

		return firstValueFrom(this._httpClient.post(endpointUrl, null, { withCredentials: true }) as Observable<IJoinCode>);
	}

	getActiveJoinCode(companyId: string): Promise<IJoinCode> {
		const endpointUrl: string = super.getEndpointUrl(HostUtil.getCompanyApiEndpointUrl(), `v1/company/${companyId}/join-code`);

		return firstValueFrom(this._httpClient.get(endpointUrl, { withCredentials: true }) as Observable<IJoinCode>);
	}

	submitJoinRequest(form: ISubmitJoinRequestForm): Promise<IJoinRequest> {
		const endpointUrl: string = super.getEndpointUrl(HostUtil.getCompanyApiEndpointUrl(), "v1/join-request");

		return firstValueFrom(this._httpClient.post(endpointUrl, { code: form.joinCode }, { withCredentials: true }) as Observable<IJoinRequest>);
	}

	getJoinRequests(companyId: string): Promise<IJoinRequest[]> {
		const endpointUrl: string = super.getEndpointUrl(HostUtil.getCompanyApiEndpointUrl(), `v1/company/${companyId}/join-request/page`);

		return firstValueFrom(this._httpClient.get(endpointUrl, {
			withCredentials: true,
			params: { pageSize: 100, pageNumber: 0 }
		}) as Observable<IPage<IJoinRequest>>).then(page => page.records);
	}

	acceptJoinRequest(companyId: string, joinRequestId: string): Promise<IOkResponse> {
		const endpointUrl: string = super.getEndpointUrl(HostUtil.getCompanyApiEndpointUrl(), `v1/company/${companyId}/join-request/${joinRequestId}/accept`);

		return firstValueFrom(this._httpClient.put(endpointUrl, null, { withCredentials: true }) as Observable<IOkResponse>);
	}

	rejectJoinRequest(companyId: string, joinRequestId: string): Promise<IOkResponse> {
		const endpointUrl: string = super.getEndpointUrl(HostUtil.getCompanyApiEndpointUrl(), `v1/company/${companyId}/join-request/${joinRequestId}/reject`);

		return firstValueFrom(this._httpClient.put(endpointUrl, null, { withCredentials: true }) as Observable<IOkResponse>);
	}

	invite(form: IInviteForm): Promise<ICompanyInvitation> {
		const endpointUrl: string = super.getEndpointUrl(HostUtil.getCompanyApiEndpointUrl(), `v1/company/${form.companyId}/invitation`);

		return firstValueFrom(this._httpClient.post(endpointUrl, { email: form.email }, { withCredentials: true }) as Observable<ICompanyInvitation>);
	}

	getInvitations(companyId: string): Promise<ICompanyInvitation[]> {
		const endpointUrl: string = super.getEndpointUrl(HostUtil.getCompanyApiEndpointUrl(), `v1/company/${companyId}/invitation/page`);

		return firstValueFrom(this._httpClient.get(endpointUrl, {
			withCredentials: true,
			params: { pageSize: 100, pageNumber: 0 }
		}) as Observable<IPage<ICompanyInvitation>>).then(page => page.records);
	}

	getMyPendingJoinRequest(): Promise<IJoinRequest | null> {
		const endpointUrl: string = super.getEndpointUrl(HostUtil.getCompanyApiEndpointUrl(), "v1/join-request/my-pending");

		return firstValueFrom(this._httpClient.get(endpointUrl, { withCredentials: true }) as Observable<IJoinRequest>)
			.catch(() => null);
	}
}
